import { CartAddEvent } from '@theme/events';
const clean = s => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const kind = p => {
  const s = clean(`${p.title} ${p.type}`);
  return /\bsocks?\b/.test(s) ? 'socks' : /\b(joggers?|sweatpants|track pants|trackies)\b/.test(s) ? 'joggers' : /\b(hoodies?|hoody|hood|hooded)\b/.test(s) ? 'hoodie' : /\b(tees?|t shirts?|tshirts?)\b/.test(s) ? 'tee' : 'other';
};
const artwork = p => clean(p.title).split(' ').filter(w => !new Set('art martial mens womens unisex kids junior adult organic premium classic oversized heavyweight black white grey gray navy blue red green pink purple cotton pullover zip up hoodie hood hooded jogger joggers sweatpants track pants tee tees t shirt shirts tshirt sweatshirt socks sock print printed clothing the a and'.split(' ')).has(w)).join(' ');
export function rankProducts(data) {
  const used = new Set(data.items.map(p => p.id));
  return [...new Map(data.products.filter(Boolean).map(p => [p.id,p])).values()].filter(p => !used.has(p.id) && p.variants.some(v => v.available)).map(p => {
    let score = 0, reason = 'extra';
    const pk = kind(p);
    if (pk === 'socks') score = 35;
    if (data.preferred.includes(p.id)) score = 45;
    for (const item of data.items) {
      const ik = kind(item);
      const linked = p.handle && (item.linked || []).includes(p.handle);
      const linkedScore = linked ? 240 - Math.min((item.linked || []).indexOf(p.handle), 30) : 0;
      if (linkedScore > score) {
        score = linkedScore;
        reason = /\b(a3|art print|riso)\b/.test(clean(p.title)) ? 'match' : 'pair';
      }
      const tags = (p.tags || []).map(clean);
      const tagged = (item.tags || []).map(clean).some(t => /^(design|print|match|range) /.test(t) && tags.includes(t));
      const a = artwork(p), b = artwork(item);
      const matching = tagged || (a.length >= 4 && a === b);
      if (matching && pk !== ik && score < 100) { score = 100; reason = 'match'; }
    }
    return { ...p, score, reason };
  }).filter(p => p.score > 0).sort((a,b) => b.score-a.score || a.id-b.id).slice(0,4);
}
class AMCartRecommendations extends HTMLElement {
  connectedCallback() {
    this.render();
    this.observer = new MutationObserver(() => this.render());
    this.observer.observe(this, {childList:true, subtree:true, characterData:true});
  }
  disconnectedCallback() { this.observer?.disconnect(); }
  render() {
    const raw = this.querySelector('[data-am-data]')?.textContent;
    const target = this.querySelector('[data-am-results]');
    if (!target || !raw || (raw === this.raw && target.childElementCount)) return;
    this.raw = raw;
    let data;
    try { data = JSON.parse(raw); } catch { return; }
    const products = rankProducts(data), labels = data.labels;
    target.replaceChildren();
    if (!products.length) { target.append(document.createElement('span')); this.hidden = true; return; }
    this.hidden = false;
    const heading = document.createElement('h3'); heading.textContent = labels.heading;
    const grid = document.createElement('div'); grid.className = 'am-rec-grid';
    target.append(heading, grid);
    const money = n => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style:'currency',currency:this.dataset.currency || 'GBP'}).format(n/100);
    for (const p of products) {
      const card = document.createElement('article'); card.className = 'am-rec-card';
      const link = document.createElement('a'); link.href = p.url;
      if (p.image) { const img = document.createElement('img'); img.src = p.image; img.alt = p.title; img.loading = 'lazy'; img.width = 180; img.height = 180; link.append(img); }
      const reason = document.createElement('small'); reason.textContent = labels[p.reason];
      const title = document.createElement('span'); title.className = 'am-rec-title'; title.textContent = p.title; link.append(reason,title);
      const price = document.createElement('span'); price.className = 'am-rec-price';
      const available = p.variants.filter(v => v.available);
      price.textContent = money(Math.min(...available.map(v => v.price)));
      const select = document.createElement('select'); select.setAttribute('aria-label', `${labels.choose}: ${p.title}`);
      select.add(new Option(labels.choose,''));
      for (const v of p.variants) { const option = new Option(`${v.title} · ${money(v.price)}`,v.id); option.disabled = !v.available; select.add(option); }
      const single = p.variants.length === 1;
      if (single) { select.value = available[0].id; select.hidden = true; }
      const button = document.createElement('button'); button.type = 'button'; button.className = 'button am-rec-add'; button.textContent = labels.add; button.disabled = !select.value;
      select.addEventListener('change', () => { button.disabled = !select.value; const v = available.find(v => String(v.id) === select.value); if(v) price.textContent = money(v.price); });
      const error = document.createElement('p'); error.className = 'am-rec-error'; error.setAttribute('role','alert'); error.hidden = true;
      button.addEventListener('click', async () => {
        if (!select.value) return;
        button.disabled = true; select.disabled = true; button.textContent = labels.adding; error.hidden = true;
        try {
          const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(e => e.dataset.sectionId))].slice(0,5);
          const response = await fetch(`${(this.dataset.root || '/').replace(/\/?$/, '/')}cart/add.js`, {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items:[{id:Number(select.value),quantity:1}],sections,sections_url:location.pathname})});
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || labels.error);
          document.dispatchEvent(new CartAddEvent(result,'am-cart-recommendations',{source:'product-form-component',itemCount:1,sections:result.sections}));
        } catch (e) { error.textContent = e.message || labels.error; error.hidden = false; }
        finally { button.disabled = false; select.disabled = false; button.textContent = labels.add; }
      });
      card.append(link,price,select,button,error); grid.append(card);
    }
  }
}
if (!customElements.get('am-cart-recommendations')) customElements.define('am-cart-recommendations',AMCartRecommendations);
